import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Box, Card, CardContent, TextField, Button, Typography, Alert, CircularProgress, Stepper, Step, StepLabel, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Email, Phone } from "@mui/icons-material";
import { sendOTP, validateOTP } from "../services/auth-service";

interface LoginPageProps {
  consentId: number;
  collectionPointId: number;
  clientId: number;
  onLoginSuccess: (jwt: string, clientData: any) => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ consentId, collectionPointId, clientId, onLoginSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [identifier, setIdentifier] = useState("");
  const [identifierType, setIdentifierType] = useState<'EMAIL' | 'PHONE'>('EMAIL');
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [rateLimited, setRateLimited] = useState(false);
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);
  const location = useLocation();

  // Check if clientId is valid
  const isValidClientId = clientId && clientId > 0;

  // Check for error context and pre-population from navigation state
  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
    }

    // Pre-populate identifier and type if available from context
    if (location.state?.context?.identifier) {
      setIdentifier(location.state.context.identifier);
      
      // Determine identifier type from context
      if (location.state?.context?.type) {
        const contextType = location.state.context.type.toUpperCase();
        if (contextType === 'PHONE') {
          setIdentifierType('PHONE');
        } else {
          setIdentifierType('EMAIL');
        }
      }
    }
  }, [location.state]);

  // Rate limiting countdown timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (rateLimited && rateLimitCountdown > 0) {
      interval = setInterval(() => {
        setRateLimitCountdown((prev) => {
          if (prev <= 1) {
            setRateLimited(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [rateLimited, rateLimitCountdown]);

  const handleSendOTP = async () => {
    if (!identifier) {
      setError(`Please enter your ${identifierType === 'EMAIL' ? 'email address' : 'phone number'}`);
      return;
    }

    // Validate identifier format
    if (identifierType === 'EMAIL') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(identifier)) {
        setError("Please enter a valid email address");
        return;
      }
    } else {
      // Phone validation (basic E.164 check)
      const cleanedPhone = identifier.replace(/[\s\-\(\)\.]/g, '');
      if (!/^\+?\d{10,15}$/.test(cleanedPhone)) {
        setError("Please enter a valid phone number (e.g., +919322595490)");
        return;
      }
    }

    // Validate clientId before making API call
    if (!isValidClientId) {
      setError("Invalid access link. Please use the link provided in your consent email or notification.");
      return;
    }

    if (rateLimited) {
      setError(`Please wait ${rateLimitCountdown} seconds before requesting another OTP`);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await sendOTP(identifier, clientId, identifierType);

      if (response.success) {
        const successMessage = identifierType === 'EMAIL'
          ? "OTP sent successfully! Please check your email."
          : "OTP sent successfully! Please check your phone.";
        setSuccess(successMessage);
        setActiveStep(1);

        // Start rate limiting (2 minutes = 120 seconds)
        setRateLimited(true);
        setRateLimitCountdown(120);
      } else {
        setError(response.error || "Failed to send OTP");
      }
    } catch (error) {
      setError("Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleValidateOTP = async () => {
    if (!otp) {
      setError("Please enter the OTP");
      return;
    }

    // Validate clientId before making API call
    if (!isValidClientId) {
      setError("Invalid access link. Please use the link provided in your consent email or notification.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await validateOTP(identifier, otp, clientId, identifierType);

      if (response.success && response.jwt && response.clientData) {
        onLoginSuccess(response.jwt, response.clientData);
      } else {
        setError(response.error || "Invalid OTP");
      }
    } catch (error) {
      setError("Failed to validate OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleIdentifierTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newType: 'EMAIL' | 'PHONE' | null
  ) => {
    if (newType !== null) {
      setIdentifierType(newType);
      setIdentifier(""); // Clear identifier when switching types
      setError("");
      setSuccess("");
    }
  };

  const steps = identifierType === 'EMAIL' 
    ? ["Enter Email", "Enter OTP"]
    : ["Enter Phone", "Enter OTP"];

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        bgcolor: "background.default",
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 500, width: "100%" }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            DP Portal Login
          </Typography>

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Enter your {identifierType === 'EMAIL' ? 'email' : 'phone number'} to access your consent management portal
          </Typography>

          {/* Show warning if clientId is invalid */}
          {!isValidClientId && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Invalid Access Link
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                This portal requires a valid access link. Please use the link provided in:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2, mb: 1 }}>
                <Typography component="li" variant="body2">
                  Your consent confirmation email
                </Typography>
                <Typography component="li" variant="body2">
                  Your consent management notification
                </Typography>
                <Typography component="li" variant="body2">
                  A link shared by the organization
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                If you received a link but it's not working, please contact the organization that sent it to you.
              </Typography>
            </Alert>
          )}

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {activeStep === 0 && (
            <Box>
              {/* Identifier Type Toggle */}
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                <ToggleButtonGroup
                  value={identifierType}
                  exclusive
                  onChange={handleIdentifierTypeChange}
                  aria-label="identifier type"
                  disabled={loading || rateLimited || !isValidClientId}
                >
                  <ToggleButton value="EMAIL" aria-label="email">
                    <Email sx={{ mr: 1 }} />
                    Email
                  </ToggleButton>
                  <ToggleButton value="PHONE" aria-label="phone">
                    <Phone sx={{ mr: 1 }} />
                    Phone
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Identifier Input Field */}
              <TextField
                fullWidth
                label={identifierType === 'EMAIL' ? "Email Address" : "Phone Number"}
                type={identifierType === 'EMAIL' ? "email" : "tel"}
                placeholder={identifierType === 'EMAIL' ? "user@example.com" : "+919322595490"}
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                disabled={loading || rateLimited || !isValidClientId}
                sx={{ mb: 3 }}
                helperText={identifierType === 'PHONE' ? "Enter phone number in E.164 format (e.g., +919322595490)" : undefined}
              />

              <Button fullWidth variant="contained" onClick={handleSendOTP} disabled={loading || rateLimited || !isValidClientId} sx={{ py: 1.5 }}>
                {loading ? <CircularProgress size={24} /> : rateLimited ? `Wait ${rateLimitCountdown}s` : "Send OTP"}
              </Button>
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                We've sent a 6-digit OTP to {identifierType === 'EMAIL' ? identifier : `your phone (${identifier.substring(0, 3)}***${identifier.substring(identifier.length - 3)})`}
              </Typography>

              <TextField
                fullWidth
                label="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                disabled={loading}
                sx={{ mb: 3 }}
                inputProps={{ maxLength: 6 }}
              />

              <Button fullWidth variant="contained" onClick={handleValidateOTP} disabled={loading} sx={{ py: 1.5, mb: 2 }}>
                {loading ? <CircularProgress size={24} /> : "Verify OTP"}
              </Button>

              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setActiveStep(0);
                  setOtp("");
                  setError("");
                  setSuccess("");
                }}
                disabled={loading}
              >
                Back to {identifierType === 'EMAIL' ? 'Email' : 'Phone'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
